import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface ServiceTypesData {
	anchor_title?: string
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
					text: string
				}
			}[]
		}
	}[]
	background_desktop?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	background_mobile?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	button?: {
		href: string
		label: string
	}
}

export const fields = [
	'anchor_title',
	'heading.title',
	'text.text',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'background_desktop.alt',
	'background_desktop.image_file.image.id',
	'background_desktop.image_file.image.filename_download',
	'background_mobile.alt',
	'background_mobile.image_file.image.id',
	'background_mobile.image_file.image.filename_download',
	'button.href',
	'button.label',
] as const

interface ServiceTypesProps {
	data?: ServiceTypesData
}

export default function ServiceTypes({ data }: ServiceTypesProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'ServiceTypes'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'ServiceTypes'} ref={ref} className='ServiceTypes relative bg-white'>
			{/* Background Image */}
			<div className='lg:absolute inset-0 w-full h-full relative aspect-[808/711] overflow-hidden'>
				<picture>
					<source
						srcSet={getReadableImageUrl(
							data?.background_desktop?.image_file?.image?.id || '',
							data?.background_desktop?.image_file?.image?.filename_disk || ''
						)}
						media='(min-width: 1024px)'
						type='image/webp'
					/>
					<Image
						src={getReadableImageUrl(
							data?.background_mobile?.image_file?.image?.id || '',
							data?.background_mobile?.image_file?.image?.filename_disk || ''
						)}
						alt={data?.background_mobile?.alt || ''}
						fill
						className='object-cover object-center lg:object-[24%_0]'
						quality={100}
					/>
				</picture>
			</div>

			{/* Content Container */}
			<div className='container relative  flex justify-end'>
				<div className='2xl:py-24 py-14 lg:w-9/12 bg-gradient-to-l from-white from-30% via-white/90 via-90% to-transparent md:pl-20'>
					{/* Heading */}
					{data?.heading?.title && (
						<FadeIn
							isVisible={isVisible}
							delay={100}
							direction='up'
							className='md:mb-9'
						>
							<h2
								className="text-center"
								dangerouslySetInnerHTML={sanitizeHTML(data.heading.title)}
							/>
						</FadeIn>
					)}

					{/* Text */}
					{data?.text?.text && (
						<FadeIn
							isVisible={isVisible}
							delay={200}
							direction='up'
							className='mb-9'
						>
							<div
								className='text-center grid gap-4'
								dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
							/>
						</FadeIn>
					)}

					{/* Cards Grid */}
					{data?.content_collection && data.content_collection.length > 0 && (
						<div className='flex flex-col gap-8 md:gap-4 mb-14'>
							{(() => {
								const cards = data.content_collection
								const rows: (typeof cards)[] = []

								// Group cards into rows of 2
								for (let i = 0; i < cards.length; i += 2) {
									rows.push(cards.slice(i, i + 2))
								}

								return rows.map((row, rowIndex) => {
									const isLastRow = rowIndex === rows.length - 1

									return (
										<div
											key={`row-${rowIndex}`}
											className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-6 ${
												!isLastRow ? 'md:border-b md:border-black md:pb-4' : ''
											}`}
										>
											{row.map((card, cardIndex) => {
												const titleItem =
													card.site_content_collections_id.collection.find(
														c => c.item.title
													)
												const textItem =
													card.site_content_collections_id.collection.find(
														c => c.item.text
													)

												return (
													<FadeIn
														key={card.id}
														isVisible={isVisible}
														delay={300 + (rowIndex * 2 + cardIndex) * 100}
														direction='up'
														className='p-6 md:p-5 flex flex-col gap-4 border border-black/20 md:border-0 rounded-lg md:rounded-none'
													>
														{titleItem?.item.title && (
															<h3
																className='text-3xl'
																dangerouslySetInnerHTML={sanitizeHTML(titleItem.item.title)}
															/>
														)}
														{textItem?.item.text && (
															<div
																className='grid gap-4'
																dangerouslySetInnerHTML={sanitizeHTML(
																	textItem.item.text
																)}
															/>
														)}
													</FadeIn>
												)
											})}
										</div>
									)
								})
							})()}
						</div>
					)}

					{/* CTA Button */}
					{data?.button && (
						<FadeIn
							isVisible={isVisible}
							delay={300 + (data?.content_collection?.length || 0) * 100}
							className='md:flex md:justify-end'
						>
							<Link href={data.button.href} className='btn'>
								{data.button.label}
							</Link>
						</FadeIn>
					)}
				</div>
			</div>
		</section>
	)
}

